import { NotifyContainer, NotifyContent, NotifyMain } from 'pages/NotificationTreatment/styles'
import React, { useState, useEffect } from 'react'
import { MdAccessTime, MdAttachment, MdClose, MdNavigateNext, MdNavigateBefore, MdOutlineAttachFile } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { IAuthenticationReducer } from 'types/autentication'
import { RootReducerState } from 'types/global'
import { Link } from 'react-router-dom'
import NotificationService from 'services/Notification'
import CountdownNotification from 'components/Countdown'
import { calculateTimeLeft, getWarningCountdownColor } from 'utils/damage'
import { InfoTexts } from 'pages/NotificationTreatment/GroupTreatmentsSteps/GroupNotificationData/Children/InfoWithoutInputs'
import { probalityOccurenceInfoMapper } from 'pages/NotifyEvent/constant_text'

const NotificationView: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()

    // Dados passados via state do grupo
    const { groupId, fromGroup, notificationIds = [], initialNotificationId } = location.state || {}

    // Função para converter valor numérico em label de frequência
    const getLabelByProbality = (probality: string) => {
        const item = probalityOccurenceInfoMapper.find((e) => e.probality === parseInt(probality));
        return item ? item.label : "-";
    };

    // Estado para controlar a notificação atual e todas as notificações
    const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0)
    const [allNotifications, setAllNotifications] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    // Seletores do Redux
    const authenticationSelector: IAuthenticationReducer = useSelector(
        (state: RootReducerState) => state.authenticationReducers
    )

    const currentUser = authenticationSelector?.dataJwt

    // Carregar todas as notificações no estado local
    useEffect(() => {
        const loadNotifications = async () => {
            if (notificationIds.length === 0) {
                // Fallback para notificação única usando dados mockados
                setAllNotifications([mockNotifications[0]])
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                console.log('🔄 Carregando notificações com IDs:', notificationIds)
                
                // Chamar o novo endpoint para buscar múltiplas notificações
                const notifications = await NotificationService.getNotificationsByIds(notificationIds)
                
                setAllNotifications(notifications)
                console.log('✅ Notificações carregadas do servidor:', notifications.length)

                // Encontrar o índice da notificação inicial baseado no ID passado via state
                if (initialNotificationId) {
                    const index = notifications.findIndex((notification: any) => 
                        notification.sys_id?.value === initialNotificationId
                    )
                    console.log('📍 Índice inicial encontrado:', index, 'para ID:', initialNotificationId)
                    if (index !== -1) {
                        setCurrentNotificationIndex(index)
                    }
                }
            } catch (error) {
                console.error('❌ Erro ao carregar notificações:', error)
                
                // Fallback para dados mockados em caso de erro
                const fallbackNotifications = notificationIds.map((id: string, index: number) => ({
                    ...mockNotifications[index % mockNotifications.length],
                    sys_id: { value: id, display_value: id }
                }))
                
                setAllNotifications(fallbackNotifications)
                console.log('⚠️ Usando dados fallback devido ao erro')
            } finally {
                setLoading(false)
            }
        }

        loadNotifications()
    }, [notificationIds, initialNotificationId])

    // Debug: monitorar mudanças no índice atual
    useEffect(() => {
        console.log('📊 Estado atualizado:', {
            currentNotificationIndex,
            allNotificationsLength: allNotifications.length,
            currentData: currentNotificationData?.number?.display_value || currentNotificationData?.sys_id?.value
        })
    }, [currentNotificationIndex, allNotifications.length])

    // Dados mockados das notificações - aqui você substituiria pela lógica real
    const mockNotifications = [
        {
            id: 'GKT52YTQL',
            number: { value: 'GKT52YTQL', display_value: 'GKT52YTQL' },
            incident_title: { value: 'Quase queda de paciente', display_value: 'Quase queda de paciente' },
            incident_description: {
                value: 'Paciente apresentou instabilidade ao caminhar no corredor, quase caindo mas foi amparado por enfermeira que passava no momento.',
                display_value: 'Paciente apresentou instabilidade ao caminhar no corredor, quase caindo mas foi amparado por enfermeira que passava no momento.'
            },
            sys_created_on: { value: '2023-03-13 12:42:00', display_value: '13/03/2023 - 12:42' },
            u_location: { value: 'location1', display_value: 'Hospital Copa Star' },
            u_sector: { value: 'sector1', display_value: 'Enfermaria 2º Andar' },
            u_damage_rating: { value: 'sem_dano', display_value: 'Sem dano' },
            u_incident_type: { value: 'quase_queda', display_value: 'Quase queda do paciente' },
            u_notifica_user: { value: 'user1', display_value: 'João da Silva' },
            state: { value: '2', display_value: 'Em tratamento' },
            u_procedimento_ou_processo_clinico: { value: 'proc_clinico', display_value: 'Procedimento ou processo clínico' },
            u_frequencia_do_evento_na_unidade: { value: 'remota', display_value: 'Remota - Dificilmente ocorre (2 vezes no mês)' },
            u_evento_com_risco_de_imagem: { value: 'nao', display_value: 'NÃO' }
        },
        {
            id: 'ABC123DEF',
            number: { value: 'ABC123DEF', display_value: 'ABC123DEF' },
            incident_title: { value: 'Erro de medicação', display_value: 'Erro de medicação' },
            incident_description: {
                value: 'Paciente recebeu medicação em dose incorreta devido à falha na conferência.',
                display_value: 'Paciente recebeu medicação em dose incorreta devido à falha na conferência.'
            },
            sys_created_on: { value: '2023-03-14 10:30:00', display_value: '14/03/2023 - 10:30' },
            u_location: { value: 'location2', display_value: 'Hospital Copa Star' },
            u_sector: { value: 'sector2', display_value: 'UTI Adulto' },
            u_damage_rating: { value: 'dano_leve', display_value: 'Dano leve' },
            u_incident_type: { value: 'erro_medicacao', display_value: 'Erro de medicação' },
            u_notifica_user: { value: 'user2', display_value: 'Maria Santos' },
            state: { value: '2', display_value: 'Em tratamento' },
            u_procedimento_ou_processo_clinico: { value: 'proc_clinico', display_value: 'Procedimento ou processo clínico' },
            u_frequencia_do_evento_na_unidade: { value: 'ocasional', display_value: 'Ocasional - Ocorre algumas vezes (1 vez por semana)' },
            u_evento_com_risco_de_imagem: { value: 'sim', display_value: 'SIM' }
        }
    ]

    // Notificação atual baseada no estado local
    const currentNotificationData = allNotifications[currentNotificationIndex] || mockNotifications[0]

    const handleNavigateNext = (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        console.log('🚀 handleNavigateNext chamado - antes:', currentNotificationIndex)

        // Navegação circular: se estiver no último, vai para o primeiro
        const nextIndex = currentNotificationIndex < allNotifications.length - 1
            ? currentNotificationIndex + 1
            : 0

        console.log('🚀 handleNavigateNext - próximo índice:', nextIndex)
        setCurrentNotificationIndex(nextIndex)
    }

    const handleNavigatePrevious = (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        console.log('🔙 handleNavigatePrevious chamado - antes:', currentNotificationIndex)

        // Navegação circular: se estiver no primeiro, vai para o último
        const prevIndex = currentNotificationIndex > 0
            ? currentNotificationIndex - 1
            : allNotifications.length - 1

        console.log('🔙 handleNavigatePrevious - próximo índice:', prevIndex)
        setCurrentNotificationIndex(prevIndex)
    }

    const handleClose = () => {
        if (fromGroup && groupId) {
            // Verificar se veio da edição do diagrama de Ishikawa
            const isFromIshikawaEdit = location.state?.fromIshikawaEdit === true
            // Verificar se veio da edição do plano de ação
            const isFromActionPlanEdit = location.state?.fromActionPlanEdit === true
            
            if (isFromIshikawaEdit) {
                // Voltar para a tela de edição do diagrama de Ishikawa
                navigate(`/notificacoes/group_ishikawa/${groupId}`)
            } else if (isFromActionPlanEdit) {
                // Voltar para a tela de edição do plano de ação
                navigate(`/notificacoes/group_action_plan/${groupId}`)
            } else {
                // Voltar para a tela principal do grupo (step padrão)
                navigate(`/notificacoes/group-treatment/${groupId}`)
            }
        } else {
            navigate('/notificacoes')
        }
    }

    // Função para fazer download do anexo
    const handleAttachmentDownload = async () => {
        try {
            if (currentNotificationData?.u_attachments_url?.value) {
                const { data }: any = await NotificationService.getPresignedUrlById(currentNotificationData.sys_id?.value)
                
                if (data) {
                    const response = await NotificationService.getAttachmentBucketS3(data)
                    const file = new File([response], currentNotificationData?.u_attachments_string?.value, { 
                        type: response.type || 'application/octet-stream' 
                    })

                    // Criar link temporário para download
                    const fileURL = URL.createObjectURL(file)
                    const link = document.createElement('a')
                    link.href = fileURL
                    link.download = file.name
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(fileURL)
                }
            }
        } catch (error) {
            console.error('Erro ao baixar anexo:', error)
        }
    }

    // Função para truncar nome do arquivo
    const getAttachmentFileName = (filename: string) => {
        if (filename) {
            const parts = filename.split('.')
            const extension = parts.pop()
            const nameWithoutExt = parts.join('.')
            
            if (nameWithoutExt.length > 15) {
                return nameWithoutExt.substring(0, 15) + '...' + extension
            }
        }
        return filename
    }

    return (
        <NotifyContainer>
            <NotifyMain>
                <NotifyContent>
                    {/* Header com breadcrumb e navegação */}
                    <div className="px-8 flex flex-row justify-between items-center">
                        <div className="text-[12px] text-darkGray/80">
                            <Link className="mr-1 cursor-pointer" to="/notificacoes">
                                Gestão das notificações
                            </Link>
                            {' > '}
                            {fromGroup && groupId ? (
                                <>
                                    <Link 
                                        className="mr-1 cursor-pointer ml-1" 
                                        to={`/notificacoes/group-treatment/${groupId}`}
                                    >
                                        Tratamento em grupo
                                    </Link>
                                    {' > '}
                                </>
                            ) : null}
                            <span className="font-semibold ml-1">Dados da notificação</span>
                        </div>

                        <div className="flex justify-end flex-wrap gap-4">
                            {/* Navigation Buttons */}
                            {(() => {
                                console.log('🔍 Debug botões:', {
                                    allNotificationsLength: allNotifications.length,
                                    loading,
                                    shouldShow: allNotifications.length > 1 && !loading
                                })
                                return allNotifications.length > 1 && !loading
                            })() && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleNavigatePrevious}
                                            className="text-sm border-[1px] rounded-lg h-[38px] inline-flex items-center hover:brightness-95 transition-all active:scale-[.97] p-4 border-blueTheme text-blueTheme"
                                            title="Notificação anterior"
                                        >
                                            <MdNavigateBefore size={16} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleNavigateNext}
                                            className="text-sm border-[1px] rounded-lg h-[38px] inline-flex items-center hover:brightness-95 transition-all active:scale-[.97] p-4 border-blueTheme text-blueTheme"
                                            title="Próxima notificação"
                                        >
                                            <MdNavigateNext size={16} />
                                        </button>
                                    </>
                                )}

                            {/* Close Button */}
                            <button
                                type="button"
                                onClick={handleClose}
                                className="text-sm border-[1px] rounded-lg h-[38px] inline-flex items-center hover:brightness-95 transition-all active:scale-[.97] px-4 bg-blueTheme text-white"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>

                    {/* Conteúdo Principal */}
                    <div className="px-[33px] py-6">
                        {loading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="text-gray-600">Carregando notificações...</div>
                            </div>
                        ) : (
                            <>
                                {/* Header da Notificação */}
                                <div className="mb-6">
                                    <h1 className="text-lg font-semibold text-blueTheme mb-4">
                                        {`Notificação ${allNotifications?.length ? (currentNotificationIndex + 1) : 1}`}
                                    </h1>

                                    <div className="grid grid-cols-4 gap-6">
                                        <div>
                                            <InfoTexts
                                                title="Chave da notificação"
                                                subtitle={currentNotificationData.number?.display_value || currentNotificationData.sys_id?.value || 'Não informado'}
                                            />
                                            <div className="text-xs text-darkGray80 italic mt-2">
                                                Circunstância notificável
                                            </div>
                                            <div className="mt-2">
                                                <span className="inline-block px-3 py-1 text-xs bg-gray-200 text-darkGray rounded-full">
                                                    {fromGroup ? 'Tratamento em grupo' : 'Individual'}
                                                </span>
                                            </div>
                                        </div>

                                        <div>
                                            <InfoTexts
                                                title="Data da notificação"
                                                subtitle={currentNotificationData.sys_created_on?.display_value || currentNotificationData.date_incident?.display_value || 'Não informado'}
                                            />
                                            <div className="mt-1">
                                                <CountdownNotification
                                                    notification={currentNotificationData}
                                                    status={currentNotificationData?.u_state?.value || currentNotificationData?.state?.value}
                                                    color={getWarningCountdownColor(currentNotificationData)}
                                                    timer={calculateTimeLeft(currentNotificationData)?.toString()}
                                                    containerStyle="flex flex-row justify-start"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <InfoTexts
                                                title="Notificante"
                                                subtitle={currentNotificationData.reporter_name?.display_value || 'Não informado'}
                                            />
                                        </div>

                                        <div>
                                            {currentNotificationData?.u_attachments_url?.value && currentNotificationData?.u_attachments_string?.value ? (
                                                <>
                                                    <span className="text-xs text-darkGray80">Anexo</span>
                                                    <div className="mt-1">
                                                        <div 
                                                            className="flex items-center text-blueTheme cursor-pointer hover:underline"
                                                            onClick={handleAttachmentDownload}
                                                            title={currentNotificationData?.u_attachments_string?.value}
                                                        >
                                                            <MdOutlineAttachFile className="w-4 h-4 mr-1" />
                                                            <span className="text-xs font-semibold">
                                                                {getAttachmentFileName(currentNotificationData?.u_attachments_string?.value)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <InfoTexts
                                                    title="Anexo"
                                                    subtitle="-"
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <InfoTexts
                                            title="Analisada e Encaminhada por"
                                            subtitle={currentNotificationData?.u_assigned_to?.display_value || '-'}
                                        />
                                    </div>
                                </div>

                                <div className="h-px bg-borderLine my-6" />

                                {/* Informações complementares da Qualidade da Unidade */}
                                <span className="text-blueTheme font-semibold text-sm">
                                    Informações complementares da Qualidade da Unidade
                                </span>

                                <div className='grid grid-cols-2 gap-6 mt-6'>
                                    <InfoTexts
                                        title="Tipo do incidente confirmado pela Qualidade da Unidade"
                                        subtitle={currentNotificationData.u_reclassified_incident_type?.display_value || '-'}
                                    />
                                    <InfoTexts
                                        title="Classificação do incidente de acordo com a OMS - Organização Mundial da Saúde"
                                        subtitle={currentNotificationData.u_oms_classification?.display_value || '-'}
                                    />

                                    <InfoTexts
                                        title="Frequência do evento na Unidade"
                                        subtitle={getLabelByProbality(currentNotificationData.u_probability_occurence?.value)}
                                    />

                                    <InfoTexts
                                        title="Evento com Risco de Imagem?"
                                        subtitleClassNames="uppercase"
                                        subtitle={currentNotificationData.u_image_risk?.display_value || '-'}
                                    />
                                </div>
                                
                                {/* Não exibir Never Events e Evento Sentinela em tratamento em grupo */}
                                {!fromGroup && (
                                    <>
                                        <div className="mt-6">
                                            <InfoTexts title="Never Events - ANVISA" subtitle="Úlcera (lesão) por pressão estágio III (perda total de espessura tecidual – tecido adiposo subcutâneo pode ser visível, sem exposição dos ossos, tendões ou músculos)"/>
                                        </div>

                                        <div className="mt-6">
                                            <InfoTexts title="Evento Sentinela - JCI" subtitle="Úlcera (lesão) por pressão estágio III (perda total de espessura tecidual – tecido adiposo subcutâneo pode ser visível, sem exposição dos ossos, tendões ou músculos)" />
                                        </div>
                                    </>
                                )}
                                
                                <div className="h-px bg-borderLine my-6" />

                                <span className="text-blueTheme font-semibold text-sm">Informações da notificação</span>

                                <div className="flex gap-[72px] mt-6">
                                    <InfoTexts
                                        title="Data da ocorrência"
                                        subtitle={currentNotificationData.date_incident?.display_value || '-'}
                                    />
                                    <InfoTexts
                                        title="Título do incidente"
                                        subtitle={currentNotificationData.incident_title?.display_value || '-'}
                                    />
                                </div>

                                <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-y-4 mt-6">
                                    <InfoTexts
                                        classNames="col-span-1"
                                        title="Unidade"
                                        subtitle={currentNotificationData.location_incident?.display_value || '-'}
                                    />
                                    <InfoTexts
                                        classNames="col-span-1"
                                        title="Setor notificado"
                                        subtitle={currentNotificationData.incident_sector?.display_value || '-'}
                                    />
                                    <InfoTexts
                                        classNames="col-span-1"
                                        title="Turno"
                                        subtitle={currentNotificationData.shift_incident?.display_value || '-'}
                                    />
                                    <InfoTexts
                                        classNames="col-span-1"
                                        title="Setor do notificante"
                                        subtitle={currentNotificationData.reporter_sector?.display_value || '-'}
                                    />
                                </div>

                                <div className="mt-6">
                                    <InfoTexts
                                        title="Descrição do incidente"
                                        subtitle={currentNotificationData.incident_description?.display_value || '-'}
                                    />
                                </div>
                                
                                <div className="mt-6">
                                    <InfoTexts
                                        title="Ação corretiva imediata"
                                        subtitle={currentNotificationData.immediate_corrective_action?.display_value || '-'}
                                    />
                                </div>

                                <div className="grid grid-cols-4 mt-6">
                                    <InfoTexts
                                        classNames="lg:col-span-1 col-span-2"
                                        title="Classificação do Incidente"
                                        subtitle={currentNotificationData.damage_level?.display_value || '-'}
                                    />
                                    <InfoTexts
                                        classNames="lg:col-span-1 col-span-2"
                                        title="Fator contribuinte"
                                        subtitle={currentNotificationData.u_categorized_contributing_factor?.display_value || '-'}
                                    />
                                </div>

                                {/* Seção condicional: Multiplicador ou Informações do paciente */}
                                {currentNotificationData?.u_number_patients_involved?.value && 
                                 currentNotificationData?.involves_patient?.value ? (
                                    <div className="grid grid-cols-4 mt-6">
                                        <InfoTexts
                                            classNames="lg:col-span-1 col-span-2"
                                            title="Quantos pacientes estão relacionados"
                                            subtitle={currentNotificationData?.u_number_patients_involved?.display_value || 'Não informado'}
                                        />
                                        <InfoTexts
                                            classNames="lg:col-span-1 col-span-2"
                                            title="Multiplicador"
                                            subtitle={currentNotificationData?.u_notification_multiplier?.display_value || 'Não informado'}
                                        />
                                    </div>
                                ) : (
                                    <div className="grid lg:grid-cols-5 md:grid-cols-2 grid-cols-1 gap-y-4 mt-6">
                                        <InfoTexts
                                            classNames="col-span-1"
                                            title="Iniciais do nome do paciente"
                                            subtitle={currentNotificationData?.patient_initials?.display_value || 'Não informado'}
                                        />
                                        <InfoTexts
                                            classNames="col-span-1"
                                            title="Data de nascimento"
                                            subtitle={currentNotificationData?.patient_date_of_birth?.display_value || 'Não informado'}
                                        />
                                        <InfoTexts
                                            title="Gênero"
                                            subtitle={currentNotificationData?.patient_gender?.display_value || 'Não informado'}
                                        />
                                        <InfoTexts
                                            classNames="col-span-1"
                                            title="Tipo de documento"
                                            subtitle={currentNotificationData?.document_type?.display_value || 'Não informado'}
                                        />
                                        <InfoTexts
                                            classNames="col-span-1"
                                            title="Número do documento"
                                            subtitle={currentNotificationData?.document_number?.display_value || 'Não informado'}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </NotifyContent>
            </NotifyMain>
        </NotifyContainer>
    )
}

export default NotificationView